import type { UIViewInjectedProps } from '@uirouter/react';

import { getTranslatedString } from '@/utils';
import { api } from '@/api';
import { Drawer } from '@/components/drawer';
import { Upload } from '@/components/upload';
import { Col, message, notification } from 'antd';
import type { RcFile } from 'antd/lib/upload';
import { waybillStore } from '@/stores';

const MAX_LINK_FARMERS_FILES = 20;

export const LinkWaybillWithFarmersFromExcel = ({ transition }: UIViewInjectedProps) => {
  const request = async (fileList: RcFile[]) => {
    if (fileList.length === 0) {
      message.error(getTranslatedString('waybills.link-with-farmers-from-excel.batch.no-files'));
      throw new Error('No files');
    }

    if (fileList.length > MAX_LINK_FARMERS_FILES) {
      message.error(
        getTranslatedString('waybills.link-with-farmers-from-excel.batch.too-many', { max: MAX_LINK_FARMERS_FILES }),
      );
      throw new Error('Too many files');
    }

    const validExtensions = ['.xls', '.xlsx'];
    const invalidFiles = fileList.filter(
      (file: RcFile) => !validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext)),
    );
    if (invalidFiles.length > 0) {
      message.error(getTranslatedString('waybills.link-with-farmers-from-excel.batch.invalid-extension'));
      throw new Error('Invalid file extension');
    }

    const formData = new FormData();
    fileList.forEach((file) => {
      formData.append('files', file);
    });

    const body = await api.waybills.linkFarmersFromExcelBatch(formData).source;
    const { results, allSucceeded } = body.data;

    const failures = results.filter((r) => !r.ok);
    const okCount = results.length - failures.length;

    const failureDescription = failures.map((r) => `${r.fileName}${r.error ? `: ${r.error}` : ''}`).join('\n');

    if (allSucceeded) {
      message.success(
        getTranslatedString('waybills.link-with-farmers-from-excel.batch.success-all', { count: results.length }),
      );
    } else if (okCount > 0) {
      notification.warning({
        message: getTranslatedString('waybills.link-with-farmers-from-excel.batch.partial-title', {
          ok: okCount,
          fail: failures.length,
        }),
        description: failureDescription || undefined,
        duration: 0,
      });
    } else {
      notification.error({
        message: getTranslatedString('waybills.link-with-farmers-from-excel.batch.all-failed-title'),
        description: failureDescription || undefined,
        duration: 0,
      });
    }

    await waybillStore.refresh();

    if (allSucceeded || okCount > 0) {
      transition.router.stateService.go('base-layout.waybills');
    }
  };

  const onClose = () => {
    transition.router.stateService.go('base-layout.waybills');
  };

  return (
    <Drawer onClose={onClose} visible={true} title={getTranslatedString('waybills.link-with-farmers-from-excel')}>
      <Col>
        <Upload
          request={request}
          suppressBuiltInSuccess={true}
          accept=".xls,.xlsx"
          title={getTranslatedString('waybills.link-with-farmers-from-excel.upload-title')}
          description={getTranslatedString('waybills.link-with-farmers-from-excel.upload-hint')}
        />
      </Col>
    </Drawer>
  );
};
